import { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioSegments } from "@/components/ui/radio-segments";
import { Textarea } from "@/components/ui/textarea";
import { investingApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";
import { formFromCompany, formFromProposal, verdictBody, verdictProblems } from "./lib/verdictForm";

const MAIN = "h-11 md:h-9";
// The API's limits (backend/investing/constants.py).
const EXIT_CONDITION_MAX = 2000;
const REASON_MAX = 1000;

const VERDICT_OPTIONS = [
  { value: "watch", label: "Watch" },
  { value: "own", label: "Own" },
  { value: "avoid", label: "Avoid" },
];
const CONVICTION_OPTIONS = [
  { value: "", label: "None" },
  ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) })),
];
// The fields in the order they're on the form: the first with a problem
// takes focus.
const FIELDS = ["verdict", "conviction", "exit_condition", "primary_thesis", "reason"];

/** A field's message(s) as one line. */
const said = (value) => [].concat(value ?? []).join(" ");

/**
 * Edit verdict (spec walkthrough 4, D6): Watch, Own or Avoid, a conviction
 * (1–5, or none), an exit condition, a primary thesis (required for Own) and
 * a reason (always — the history keeps it). Saving replaces the WHOLE
 * verdict: what's left empty is cleared.
 *
 * Opened with a `proposal`, it starts from what research proposed ("Decide
 * on this proposal"); otherwise from the company's verdict. The server is
 * the judge — an agent refused by D6 (a person set this verdict) sees the
 * server's own words. `onSaved(page)` gets the company's page as the PATCH
 * returned it. Static backdrop; Escape and Cancel are refused while saving.
 *
 * The theses are the page's: while they load the choice says so, and when
 * they couldn't be loaded the dialog says that — not just that an Own needs
 * one — with a Retry (`onRetryTheses`). A failed save that isn't about a
 * field is brought into view and focused (lib/reveal.js).
 */
function VerdictDialog({
  open,
  company,
  proposal = null,
  theses,
  thesesLoading = false,
  thesesError = null,
  onRetryTheses = null,
  me,
  onOpenChange,
  onSaved,
  onCloseAutoFocus,
}) {
  const id = useId();
  const symbol = company?.symbol ?? "";
  const [form, setForm] = useState(() => formFromCompany(company));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const verdictRef = useRef(null);
  const formErrorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);
  const fieldRefs = {
    conviction: useRef(null),
    exit_condition: useRef(null),
    primary_thesis: useRef(null),
    reason: useRef(null),
  };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Each opening starts from the proposal, or the verdict as it is now —
  // set while rendering the opening (React's "adjust state on a prop
  // change"), not in an effect: Radix focuses the first field in its own
  // mount effect, which runs before ours, so an effect would leave focus on
  // the last opening's choice.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setForm(proposal ? formFromProposal(proposal) : formFromCompany(company));
      setErrors({});
      setFormError(null);
      setSaving(false);
    }
  }

  const focusField = (name) => {
    if (name === "verdict" || name === "conviction") {
      const group = name === "verdict" ? verdictRef.current : fieldRefs.conviction.current;
      (group?.querySelector("input:checked") ?? group?.querySelector("input"))?.focus();
      return;
    }
    fieldRefs[name]?.current?.focus();
  };

  const set = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const save = async (event) => {
    event.preventDefault();
    // No busy check: the submit button is disabled while saving, and a form
    // whose default button is disabled can't be submitted implicitly.
    const problems = verdictProblems(form);
    if (Object.keys(problems).length) {
      setErrors(problems);
      setFormError(null);
      focusField(FIELDS.find((name) => problems[name]));
      return;
    }
    setSaving(true);
    setErrors({});
    setFormError(null);
    try {
      const { data } = await investingApi.companies.verdict(symbol, verdictBody(form));
      if (mounted.current) setSaving(false);
      onSaved(data);
    } catch (err) {
      if (!mounted.current) return;
      setSaving(false);
      const parsed = parseApiError(err, "Couldn't save the verdict.");
      const known = Object.fromEntries(
        Object.entries(parsed.fieldErrors).filter(([name]) => FIELDS.includes(name)),
      );
      // The server's words for a retired primary thesis are an API's
      // ("… theses/ lists them"): say it as it is for the one chosen.
      if (known.primary_thesis && theses.find((t) => t.slug === form.primary_thesis)?.status === "retired") {
        known.primary_thesis = "That thesis is retired — pick an active one.";
      }
      const other = Object.entries(parsed.fieldErrors)
        .filter(([name]) => !FIELDS.includes(name))
        .map(([name, value]) => `${name}: ${said(value)}`);
      setErrors(known);
      setFormError([parsed.formError, ...other].filter(Boolean).join(" — ") || null);
      const first = FIELDS.find((name) => known[name]);
      if (first) setTimeout(() => focusField(first), 0);
      else revealLater(formErrorRef);
    }
  };

  // The primary-thesis choices: active theses, plus the one in the form if
  // it isn't among them (retired, or not listed) — kept, never dropped.
  const active = theses.filter((t) => t.status === "active");
  const options = active.map((t) => ({ value: t.slug, label: t.title }));
  if (form.primary_thesis && !active.some((t) => t.slug === form.primary_thesis)) {
    const known = theses.find((t) => t.slug === form.primary_thesis);
    options.push({
      value: form.primary_thesis,
      label: known ? `${known.title}${known.status === "retired" ? " (retired)" : ""}` : form.primary_thesis,
    });
  }

  const errorId = (name) => `${id}-${name}-error`;
  const fieldError = (name) =>
    errors[name] ? (
      <p id={errorId(name)} className="m-0 text-sm text-destructive">
        {said(errors[name])}
      </p>
    ) : null;
  const describedBy = (name, help) =>
    [help ? `${id}-${name}-help` : null, errors[name] ? errorId(name) : null].filter(Boolean).join(" ") ||
    undefined;

  const title = proposal ? `Decide on the proposal for ${symbol}` : `Edit ${symbol}'s verdict`;

  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      {/* data-investing: portalled out of the layout, so it carries the
          scope that keeps its muted text AA (tailwind.css). */}
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => saving && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          focusField("verdict");
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription>
            Saving replaces the whole verdict — anything left empty is cleared — and the history keeps
            your reason.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} noValidate className="flex min-h-0 flex-1 flex-col">
          <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            {proposal && (
              <p className="m-0 text-sm">
                Pre-filled from note #{proposal.note}&apos;s proposal
                {proposal.by?.username ? `, by ${proposal.by.username}` : ""}. Change anything before you save.
              </p>
            )}
            {me?.kind === "agent" && (
              <p className="m-0 text-sm text-muted-foreground">
                As an agent you can set a verdict only while it&apos;s Unreviewed or an agent set it last;
                otherwise propose one in a research note, and the person decides.
              </p>
            )}
            <div ref={verdictRef} className="flex flex-col gap-1.5">
              <RadioSegments
                legend="Verdict"
                options={VERDICT_OPTIONS}
                value={form.verdict}
                disabled={saving}
                onChange={(value) => set("verdict", value)}
              />
              {fieldError("verdict")}
            </div>
            <div ref={fieldRefs.conviction} className="flex flex-col gap-1.5">
              <RadioSegments
                legend="Conviction"
                options={CONVICTION_OPTIONS}
                value={form.conviction}
                disabled={saving}
                onChange={(value) => set("conviction", value)}
              />
              <p className="m-0 text-xs text-muted-foreground">1 is a hunch, 5 as sure as you get.</p>
              {fieldError("conviction")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-exit_condition`}>Exit condition</Label>
              <Textarea
                ref={fieldRefs.exit_condition}
                id={`${id}-exit_condition`}
                value={form.exit_condition}
                maxLength={EXIT_CONDITION_MAX}
                placeholder="e.g. Hyperscaler capex falls two quarters running"
                className="max-h-48 min-h-16"
                disabled={saving}
                aria-invalid={errors.exit_condition ? true : undefined}
                aria-describedby={describedBy("exit_condition", true)}
                onChange={(event) => set("exit_condition", event.target.value)}
              />
              <p id={`${id}-exit_condition-help`} className="m-0 text-xs text-muted-foreground">
                What would make you sell. Optional; empty clears it.
              </p>
              {fieldError("exit_condition")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-primary_thesis`}>Primary thesis</Label>
              <NativeSelect
                ref={fieldRefs.primary_thesis}
                id={`${id}-primary_thesis`}
                value={form.primary_thesis}
                className={MAIN}
                disabled={saving}
                aria-invalid={errors.primary_thesis ? true : undefined}
                aria-describedby={
                  [describedBy("primary_thesis", true), thesesError ? `${id}-theses-error` : null].filter(Boolean).join(" ")
                }
                onChange={(event) => set("primary_thesis", event.target.value)}
              >
                <option value="">{thesesLoading ? "Loading the theses…" : "None"}</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
              <p id={`${id}-primary_thesis-help`} className="m-0 text-xs text-muted-foreground">
                The thesis the call rests on — required for Own, and it must be an active one.
              </p>
              {thesesError && (
                <Alert id={`${id}-theses-error`} variant="destructive">
                  <AlertDescription className="flex flex-wrap items-center gap-2">
                    <span>
                      Couldn&apos;t load the theses ({apiErrorMessage(thesesError, "error")}), so there&apos;s none to
                      pick — an Own verdict needs one.
                    </span>{" "}
                    {onRetryTheses && (
                      <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => onRetryTheses()}>
                        Retry{" "}
                        <span className="sr-only">loading the theses</span>
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              {fieldError("primary_thesis")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason</Label>
              <Textarea
                ref={fieldRefs.reason}
                id={`${id}-reason`}
                value={form.reason}
                maxLength={REASON_MAX}
                placeholder="e.g. The data-centre lead held through Q2; capex guides up again"
                className="max-h-48 min-h-20"
                disabled={saving}
                aria-invalid={errors.reason ? true : undefined}
                aria-describedby={describedBy("reason", true)}
                onChange={(event) => set("reason", event.target.value)}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                Required. Kept in the verdict history beside your name.
              </p>
              {fieldError("reason")}
            </div>
            {formError && (
              <Alert ref={formErrorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              className={MAIN}
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className={MAIN} disabled={saving} {...submitGuard}>
              {saving && <Loader2 className="animate-spin" aria-hidden="true" />}
              {saving ? "Saving the verdict…" : "Save the verdict"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

VerdictDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The company's page: `symbol` and the verdict fields. */
  company: PropTypes.shape({
    symbol: PropTypes.string.isRequired,
    verdict: PropTypes.string,
    conviction: PropTypes.number,
    exit_condition: PropTypes.string,
    primary_thesis: PropTypes.string,
  }).isRequired,
  /** A pending proposal to start from (Decide on this proposal). */
  proposal: PropTypes.shape({
    note: PropTypes.number,
    verdict: PropTypes.string,
    conviction: PropTypes.number,
    exit_condition: PropTypes.string,
    primary_thesis: PropTypes.string,
    by: PropTypes.shape({ username: PropTypes.string }),
  }),
  /** Every thesis (InvestingThesisRow): the active ones are the choices. */
  theses: PropTypes.arrayOf(
    PropTypes.shape({ slug: PropTypes.string.isRequired, title: PropTypes.string, status: PropTypes.string }),
  ).isRequired,
  /** The theses are still loading: the choice says so. */
  thesesLoading: PropTypes.bool,
  /** Listing the theses failed: said under the choice, with Retry. */
  thesesError: PropTypes.object,
  /** Ask for the theses again. */
  onRetryTheses: PropTypes.func,
  /** Who's signed in: an agent is told the D6 rule up front. */
  me: PropTypes.shape({ kind: PropTypes.string }),
  onOpenChange: PropTypes.func.isRequired,
  /** `(page)`: saved; the company's page as the PATCH returned it. */
  onSaved: PropTypes.func.isRequired,
  /** Radix's close autofocus: the page moves focus when the opener went away. */
  onCloseAutoFocus: PropTypes.func,
};

export default VerdictDialog;
